let m_requestId = 0;
let m_performanceOperationId = 0;
let m_workflowPerformanceMonitoringEnabled = false;
let m_performanceScopeCount = 0;
let m_performancePostrollCount = 0;
let m_performanceMonitorStarted = false;
let m_performanceMonitorGeneration = 0;
const m_activePerformanceOperations = new Map<number, {
  readonly name: string;
  readonly startedAt: number;
  readonly details?: Readonly<Record<string, unknown>>;
}>();

const K_FRAME_GAP_LOG_THRESHOLD_MS = 100;
const K_OPERATION_LOG_THRESHOLD_MS = 16;
/**
 * ⚠ The material-texture settle runs PAST the scene build that opened the
 * scope, so this window decides whether the tail of a load is logged at all.
 * It was briefly widened to 20 000 while diagnosing AI-1339, where an asset
 * drip-fed textures for ~8 s after `scene render ensure` reported done and the
 * log closed mid-settle. Sharing those textures collapsed that tail to a couple
 * of material passes, so 3 000 covers it again — but widen it first, rather
 * than concluding a load has finished, if a capture ends on a still-climbing
 * `EntityRenderCensus`.
 */
const K_WORKFLOW_MONITOR_POSTROLL_MS = 3_000;

export function nextSceneNavigationDiagnosticId(): number {
  return ++m_requestId;
}

export function captureSceneNavigationCaller(): string {
  const stack = new Error().stack;
  if (!stack) return "unavailable";
  return stack
    .split("\n")
    .slice(2, 8)
    .map((line) => line.trim())
    .join(" <- ");
}

export function writeSceneNavigationDiagnostic(
  source: string,
  message: string,
  details?: Readonly<Record<string, unknown>>,
): void {
  if (!import.meta.env.DEV) return;
  const suffix = details ? ` ${safeJson(details)}` : "";
  const line = `${new Date().toISOString()} [${source}] ${message}${suffix}`;
  void fetch("/__campfire_scene_navigation_log", {
    method: "POST",
    headers: { "content-type": "text/plain; charset=utf-8" },
    body: line,
    keepalive: true,
  }).catch(() => {
    // Diagnostics must never affect scene navigation.
  });
}

export function setWorkflowPerformanceMonitoring(enabled: boolean): void {
  if (!import.meta.env.DEV) return;
  const generation = ++m_performanceMonitorGeneration;
  if (enabled) {
    m_workflowPerformanceMonitoringEnabled = true;
    if (!m_performanceMonitorStarted) startFrameGapMonitor();
    return;
  }
  window.setTimeout(() => {
    if (m_performanceMonitorGeneration === generation) {
      m_workflowPerformanceMonitoringEnabled = false;
    }
  }, K_WORKFLOW_MONITOR_POSTROLL_MS);
}

/**
 * Arm the same frame-gap/operation monitor for project and scene rendering,
 * not only workflow playback. Scopes can overlap when a navigation supersedes
 * an in-flight build; each keeps its own post-roll so the final shader/link or
 * first-draw stall is still captured.
 */
export function beginSceneBuildPerformanceMonitoring(
  details: Readonly<Record<string, unknown>>,
): () => void {
  return beginPerformanceMonitoring("SceneBuildPerformance", details);
}

export function beginRuntimePerformanceMonitoring(
  details: Readonly<Record<string, unknown>>,
): () => void {
  return beginPerformanceMonitoring("RuntimePerformance", details);
}

function beginPerformanceMonitoring(
  source: string,
  details: Readonly<Record<string, unknown>>,
): () => void {
  if (!import.meta.env.DEV) return () => undefined;
  m_performanceScopeCount++;
  if (!m_performanceMonitorStarted) startFrameGapMonitor();
  const startedAt = performance.now();
  writeSceneNavigationDiagnostic(source, "monitoring started", details);
  let ended = false;
  return () => {
    if (ended) return;
    ended = true;
    m_performanceScopeCount = Math.max(0, m_performanceScopeCount - 1);
    m_performancePostrollCount++;
    writeSceneNavigationDiagnostic(source, "monitoring completed", {
      durationMs: roundMs(performance.now() - startedAt),
      ...details,
    });
    window.setTimeout(() => {
      m_performancePostrollCount = Math.max(
        0,
        m_performancePostrollCount - 1,
      );
    }, K_WORKFLOW_MONITOR_POSTROLL_MS);
  };
}

/**
 * Write a diagnostic only while a monitoring scope (workflow playback, project
 * load, scene build, runtime visibility change) is armed. Per-frame callers use
 * this so an idle session writes nothing.
 */
export function writePerformanceDiagnostic(
  source: string,
  message: string,
  details: Readonly<Record<string, unknown>>,
): void {
  if (!import.meta.env.DEV || !isPerformanceMonitoringEnabled()) return;
  writeSceneNavigationDiagnostic(source, message, details);
}

export function beginWorkflowPerformanceOperation(
  name: string,
  details?: Readonly<Record<string, unknown>>,
): () => void {
  if (!import.meta.env.DEV || !isPerformanceMonitoringEnabled()) return () => undefined;
  const id = ++m_performanceOperationId;
  const startedAt = performance.now();
  m_activePerformanceOperations.set(id, { name, startedAt, details });
  return () => {
    const operation = m_activePerformanceOperations.get(id);
    if (!operation) return;
    m_activePerformanceOperations.delete(id);
    const durationMs = performance.now() - operation.startedAt;
    if (durationMs < K_OPERATION_LOG_THRESHOLD_MS) return;
    writeSceneNavigationDiagnostic("WorkflowPerformance", "operation completed", {
      name: operation.name,
      durationMs: roundMs(durationMs),
      ...operation.details,
    });
  };
}

export function runWorkflowPerformanceOperation<T>(
  name: string,
  details: Readonly<Record<string, unknown>>,
  operation: () => T,
): T {
  const end = beginWorkflowPerformanceOperation(name, details);
  try {
    return operation();
  } finally {
    end();
  }
}

export async function runWorkflowPerformanceOperationAsync<T>(
  name: string,
  details: Readonly<Record<string, unknown>>,
  operation: () => Promise<T>,
): Promise<T> {
  const end = beginWorkflowPerformanceOperation(name, details);
  try {
    return await operation();
  } finally {
    end();
  }
}

function startFrameGapMonitor(): void {
  m_performanceMonitorStarted = true;
  let previousFrameAt = performance.now();
  const onFrame = (now: number): void => {
    const gapMs = now - previousFrameAt;
    previousFrameAt = now;
    if (
      isPerformanceMonitoringEnabled()
      && document.visibilityState === "visible"
      && gapMs >= K_FRAME_GAP_LOG_THRESHOLD_MS
    ) {
      writeSceneNavigationDiagnostic("WorkflowPerformance", "animation frame gap", {
        gapMs: roundMs(gapMs),
        activeOperations: [...m_activePerformanceOperations.values()].map((operation) => ({
          name: operation.name,
          elapsedMs: roundMs(now - operation.startedAt),
          ...operation.details,
        })),
      });
    }
    requestAnimationFrame(onFrame);
  };
  requestAnimationFrame(onFrame);
}

function isPerformanceMonitoringEnabled(): boolean {
  return (
    m_workflowPerformanceMonitoringEnabled
    || m_performanceScopeCount > 0
    || m_performancePostrollCount > 0
  );
}

function roundMs(value: number): number {
  return Math.round(value * 10) / 10;
}

function safeJson(value: Readonly<Record<string, unknown>>): string {
  try {
    return JSON.stringify(value);
  } catch {
    return '{"serialization":"failed"}';
  }
}
